import appInfo from "@common/appInfo";
import { type AppContextAi } from "@common/contexts/App";
import { useI18n } from "@common/hooks/i18n";
import { updateItemBy } from "@common/lib/array";
import { EMPTY_GUID } from "@common/lib/consts";
import { usePromise } from "@common/lib/hooks";
import { uuid } from "@common/lib/misc";
import React from "react";
import { type Agent, type AiChatApi, type AiChatValue, type Chat, type ChatMessage, type SendMessageResponse } from "./types";

export type UseAiChatValueArgs = {
  /** update to reload the agent */
  agentHash?: string;
  api: AiChatApi;
  currentChatId: string | null;
};
export const useAiChatValue = (args: UseAiChatValueArgs): AiChatValue => {
  const i18n = useI18n();

  const [lastResponseId, setLastResponseId] = React.useState<string | null>(null);
  const [chatsReloadHash, setChatsReloadHash] = React.useState<string | null>(null);
  const [shouldLoadData, setShouldLoadData] = React.useState(true);

  const [agent, isAgentLoading] = usePromise<Agent>({
    fn: args.api.agents.get,
    args: [],
    shouldLoad: true,
    reload: args.agentHash
  });

  const [data, isDataLoading, setData] = usePromise<ChatMessage[]>({
    fn: args.api.chatMessages.getAll,
    args: [args.currentChatId],
    shouldLoad: !!args.currentChatId && shouldLoadData
  });

  React.useEffect(() => (args.currentChatId ? setShouldLoadData(true) : setData([])), [args.currentChatId]);

  const [chats, isChatsLoading] = usePromise<Chat[]>({
    fn: args.api.chats.getAll,
    args: [agent?.agentId],
    shouldLoad: !!agent,
    reload: chatsReloadHash
  });

  return {
    agentName: agent?.name ?? "",
    chats: chats ?? [],
    sendMessage: async (input: string) => {
      // update the data first, so it appears in the UI
      const aiChatMessageId = uuid();
      setData([
        ...(data ?? []),
        {
          message: input,
          isAI: false,
          chatMessageId: uuid(),
          createdOnDate: new Date(),
          externalId: null
        },
        {
          message: null,
          isAI: true,
          chatMessageId: aiChatMessageId,
          createdOnDate: new Date(),
          externalId: null
        }
      ]);

      let response: SendMessageResponse;
      try {
        response = await args.api.chats.sendMessage(args.currentChatId ?? EMPTY_GUID, {
          agentId: agent!.agentId,
          input,
          previousResponseId: lastResponseId,
          userName: appInfo.user?.userFirstName ?? i18n.guest
        });
        if (args.currentChatId !== response.chatId) {
          setChatsReloadHash(uuid());
        }
        setShouldLoadData(false);
      } catch (e) {
        response = {
          chatId: args.currentChatId,
          responseId: "",
          text: e.message ?? i18n.errorSomethingWentWrong
        };
      }

      if (response.responseId) {
        setLastResponseId(response.responseId);
      }

      setData((x) =>
        updateItemBy(
          x,
          (y) => y.chatMessageId === aiChatMessageId,
          (y) => ({
            ...y,
            message: response.text
          })
        )
      );

      return response.chatId;
    },
    isAvailable: !!agent?.isReady,
    isLoading: isAgentLoading || isDataLoading || isChatsLoading,
    data: data ?? [],
    welcomeMessage: agent?.isReady ? i18n.infoWelcomeMessage : i18n.infoStillLearning
  };
};

export const useAiContextValue = (): AppContextAi => {
  const [balance, setBalance] = React.useState<number>(appInfo.ai.creditBalance);

  const updateBalance = React.useCallback((credits: number) => setBalance((x) => x + credits), []);

  return {
    balance,
    updateBalance
  };
};
